/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState, useRef, useMemo, useEffect } from 'react'
import Box from '@mui/material/Box'
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTheme } from '@mui/material/styles'
import { useParams, useSearchParams } from 'react-router-dom'
import { useAppDispatch } from '../../../../store'
import { getSlotSectionApi } from '../../../../store/slices/distributionPlanSlice/apis'
import GenericDateTimePicker from '../../../../components/GenericDateTimePicker'
import { formatDateWithTime } from '../../../../helpers/formatDateWithTime'
import FormInput from '../../../../components/FormElements/FormInput'
import { Alert, AlertTitle, Link, Tab, Typography } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { SlotStatus, type ISlot } from '../../../../models/DistributionPlan'
import { type ISection } from '../../../../models/TermsSection'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { red, shade } from '../../../../colors'
import api from '../../../../utils/api'
import { ApiAutoComplete } from '../../../../components/FormElements/ApiAutoComplete'
import MultiSelectApiAutoComplete from '../../../../components/FormElements/MultiSelectApiAutoComplete'
import { type IUser } from '../../../../models/User'
import ValidationAlert from '../../../../components/ValidationAlert'
import { bindSetError, showServerFieldErrors } from '../../../../utils/showServerFieldErrors'
import { notification } from '../../../../components/Notifications'
import { ReleaseMode } from '../../../../helpers/parseReleaseTimestamp'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { StudentsAndEnrollment } from '../../../StudentsAndEnrollmentFeature'
import { getPagePermissions } from '../../../../store/slices/roleSlice/selectors'
import ROUTES from '../../../../consts/paths'
import AttributesMultiSelect from '../../../../components/FormElements/AttributesMultiSelect'
import { LastUpdateMeta } from '../../../../components/GenericModal/ChangeMetaInfo'
import { courseSectionSchema, type CourseSectionFormContext } from '../../../DistributionSeriesAndPlan/DistributionPlan/schema'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import BulkSelectCourseSectionModal from '../BulkAddCourseSectionModal'
import { type BulkAddSectionDateValues } from '../../../../helpers/BulkAddCourseSection'
import { useAccess } from '../../../../hooks/useAccess'
import { READONLY_TOOLTIP } from '../../../../consts'
import { useSlotTaskContext } from '../../../../contexts/SlotTaskContext'
import { getEnqueuedTaskIds } from '../../../../utils/enqueuedTaskIds'
import { isInstructorReleaseOnGradePostingEnabled } from '../../../../consts/messages'
import GenericButton from '../../../../components/GenericButton'
import { ButtonType } from '../../../../components/GenericButton/styles'
import CustomRadioButtonGroup from '../../../../components/CustomRadioButtonGroup'

/** Full-width secondary action container (mirrors the Add Role button pattern). */
const BulkSelectButtonStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '24px',
  padding: '24px',
  borderRadius: '8px',
  background: shade[750]
}

/** Tri-state opt-out form value. `unchanged` (default) omits isOptedOut. */
type OptOutState = 'unchanged' | 'out' | 'in'

const OPT_OUT_OPTIONS = [
  { id: 'unchanged', label: "Don't change" },
  { id: 'out', label: 'Opted out' },
  { id: 'in', label: 'Opted in' }
]

const resolveIsOptedOut = (state: OptOutState): boolean | undefined =>
  state === 'out' ? true : state === 'in' ? false : undefined

interface FormSubmissionPayload {
  sectionId: string
  startTimestamp?: string
  endTimestamp?: string
  earlyAccessReleaseTimestamp_1?: string
  earlyAccessReleaseTimestamp_2?: string | null
}

interface CreateAndEditCoursesProps {
  selectedRow?: ISection
  selectedRowWithActions?: any
  slot: ISlot
  handleCloseModal?: () => void
  /** Bulk Update mode — update already-attached sections (dates + opt-out) only. */
  bulkUpdate?: boolean
}

interface RawUserRef {
  id?: string
  userId?: string
  user_id?: string
  title?: string
  name?: string
  email?: string
}

const toFormUsers = (users: RawUserRef[]): Array<{ userId?: string, name?: string, email?: string }> =>
  users.map(({ userId, user_id, id, name, title, email }) => ({
    userId: userId ?? user_id ?? id,
    name: name ?? title,
    email
  }))

export const CreateAndEditCourses: React.FC<CreateAndEditCoursesProps> = ({
  selectedRow,
  selectedRowWithActions,
  slot,
  handleCloseModal = () => { },
  bulkUpdate = false
}) => {
  const { readonly } = useAccess(selectedRowWithActions?.actions ?? [])
  const { checkIfBlocked, setTask } = useSlotTaskContext()
  const [loading, setLoading] = useState<boolean>(false)
  const [errors, setErrors] = useState<unknown>()
  const [isBulkAddCourseSectionModalOpen, setBulkAddCourseSectionOpen] = useState<boolean>(false)
  const [bulkSectionDates, setBulkSectionDates] = useState<BulkAddSectionDateValues | undefined>()
  const [bulkUpdateOptedOut, setBulkUpdateOptedOut] = useState<boolean | undefined>()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const isEditAction = !!selectedRow
  // Opt-out is settable only when enabled on the slot (academic-unit or instructor).
  const isOptOutEnabled = Boolean(slot.isAcademicUnitOptOutEnabled) || Boolean(slot.isInstructorOptOutEnabled)

  useEffect(() => {
    if (selectedRow == null) return
    void checkIfBlocked().then((blocked) => { if (blocked) handleCloseModal() })
  }, [selectedRow])
  const { linkedSection, studentCount, primaryInstructors, endTimestamp, startTimestamp, earlyAccessReleaseTimestamp_1, earlyAccessReleaseTimestamp_2 } = selectedRow ?? {}
  const attributes = selectedRow?.linkedSection?.attributes ?? []
  const { abbreviation, sectionId } = linkedSection ?? {}
  const [tab, setTab] = useState('1')
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const sectionStatus = selectedRow?.slotStatus
  const pagePermissions = getPagePermissions()
  const isAllowedOnTermsPage = pagePermissions?.includes('Admin Settings - Terms')

  const selectedInstructors = toFormUsers((selectedRow as any)?.instructors ?? primaryInstructors ?? [])
  const selectedTeachingAssistants = toFormUsers(selectedRow?.teachingAssistants ?? [])

  const isSectionDraft = sectionStatus === SlotStatus.Draft
  const isSectionPublished = sectionStatus === SlotStatus.Published
  const isSectionLive = sectionStatus === SlotStatus.Live
  const isSectionClosed = sectionStatus === SlotStatus.Closed
  const canEditStartDate = !isEditAction || isSectionDraft || isSectionPublished
  const canEditEndDate = !isEditAction || isSectionDraft || isSectionPublished || isSectionLive
  const canEditReleaseDates = !isEditAction || isSectionDraft || isSectionPublished || isSectionLive || isSectionClosed
  const isRollingRelease = slot.releaseTimestamp === ReleaseMode.Rolling
  const slotReleaseDate = !isRollingRelease ? slot.releaseTimestamp : undefined
  const isGradePostingReleaseEnabled = isInstructorReleaseOnGradePostingEnabled(slot?.isInstructorReleaseOnGradePostingEnabled)

  const lastChangedField = useRef<'start' | 'end' | 'earlyAccess1' | 'earlyAccess2' | null>(null)
  const startMin = useMemo(
    () => new Date(slot.slotStartTimestamp) > new Date() ? new Date(slot.slotStartTimestamp) : new Date(),
    [slot.slotStartTimestamp]
  )

  // lastChangedField intentionally excluded from deps — ref.current is read at invocation time.
  const resolver = useMemo(() => {
    const base = yupResolver(courseSectionSchema)
    return async (values: Parameters<typeof base>[0], _context: Parameters<typeof base>[1], options: Parameters<typeof base>[2]) =>
      await base(values, {
        canEditStartTimestamp: canEditStartDate,
        canEditEndDate,
        canEditReleaseDates,
        isRollingRelease,
        slotReleaseTimestamp: isRollingRelease ? undefined : slot.releaseTimestamp instanceof Date ? slot.releaseTimestamp.toISOString() : slot.releaseTimestamp,
        lastChangedField: lastChangedField.current
      } satisfies CourseSectionFormContext, options)
  }, [canEditStartDate, canEditEndDate, canEditReleaseDates, isRollingRelease, slot.releaseTimestamp])

  const methods = useForm<any>({
    resolver,
    defaultValues: {
      sectionId: linkedSection?.sectionId ?? null,
      studentCount,
      title: linkedSection?.title,
      abbreviation: linkedSection?.abbreviation,
      // Bulk Update: empty = leave each section's current value unchanged.
      // Create: pre-fill from project defaults. Edit: pre-fill from the section.
      startTimestamp: bulkUpdate
        ? undefined
        : (isEditAction ? (startTimestamp ?? undefined) : (slot.slotStartTimestamp ?? undefined)),
      endTimestamp: bulkUpdate
        ? undefined
        : (isEditAction ? (endTimestamp ?? undefined) : (slot.slotEndTimestamp ?? undefined)),
      instructors: selectedInstructors,
      teachingAssistants: selectedTeachingAssistants,
      earlyAccessReleaseTimestamp1: bulkUpdate
        ? undefined
        : (isEditAction
            ? (earlyAccessReleaseTimestamp_2 ?? undefined)
            : (isGradePostingReleaseEnabled ? undefined : slotReleaseDate)),
      earlyAccessReleaseTimestamp2: bulkUpdate
        ? undefined
        : (isEditAction ? (earlyAccessReleaseTimestamp_1 ?? undefined) : slotReleaseDate),
      attributes: linkedSection?.attributes ?? [],
      optOutState: 'unchanged' as OptOutState
    },
    mode: 'onChange'
  })
  const { handleSubmit, control, watch, trigger, getValues, formState: { isDirty, isSubmitting, dirtyFields } } = methods

  const getRefreshParams = (): Record<string, string | number | undefined> => ({
    search: searchParams.get('search') ?? '',
    scope: searchParams.get('scope') ?? 'my',
    sort: searchParams.get('sort') ?? undefined,
    page: searchParams.get('page') ?? undefined,
    pageSize: Number(searchParams.get('pageSize') ?? 15),
    view: searchParams.get('view') ?? undefined
  })

  const onSubmit: SubmitHandler<any> = async (values) => {
    const payload: FormSubmissionPayload = {
      sectionId: values.sectionId,
      ...(canEditStartDate && (!isEditAction || dirtyFields.startTimestamp) && {
        startTimestamp: formatDateWithTime(values.startTimestamp)
      }),
      ...(canEditEndDate && (!isEditAction || dirtyFields.endTimestamp) && {
        endTimestamp: formatDateWithTime(values.endTimestamp)
      })
    }

    if (!isRollingRelease) {
      const sendEa1 = !isEditAction || dirtyFields.earlyAccessReleaseTimestamp1
      const sendEa2 = !isEditAction || dirtyFields.earlyAccessReleaseTimestamp2
      if (sendEa1) {
        if (values.earlyAccessReleaseTimestamp1) {
          payload.earlyAccessReleaseTimestamp_2 =
            formatDateWithTime(values.earlyAccessReleaseTimestamp1)
        } else if (isEditAction && isGradePostingReleaseEnabled) {
          payload.earlyAccessReleaseTimestamp_2 = null
        }
      }
      if (sendEa2 && values.earlyAccessReleaseTimestamp2) {
        payload.earlyAccessReleaseTimestamp_1 =
          formatDateWithTime(values.earlyAccessReleaseTimestamp2)
      }
    }

    try {
      if (!id) return

      setLoading(true)

      if (isEditAction) {
        await api.patch(
          `/api/slots/${id}/sections/${linkedSection?.sectionId}`,
          payload
        )
        notification('Section updated successfully', { variant: 'success' })
      } else {
        // Asked at submit, not just when the modal opened (`CoursesTopbarActions`) — a bulk run can
        // start on this project while the form is sitting open, including from another tab, and
        // registering the seeds below would take its key and with it the per-section outcomes that
        // are the only report of what it refused. `checkIfBlocked` clears a task that has actually
        // finished, so this only stops the add while one is genuinely still applying.
        // Found by review on #1939.
        if (await checkIfBlocked()) return
        const response = await api.post(`/api/slots/${id}/sections/`, payload)
        // Adding a section to a project with default question sets attached seeds those questions
        // in the background (Backend CR-336), one task per set. The section record itself is
        // already saved, so the refresh below stays where it is — but the questions are not, and
        // without registering the tasks the page reports the add as fully done and leaves the
        // user refreshing an incomplete section. Copy stays kind-agnostic: nothing in the
        // response says which question set an id belongs to.
        const seedTaskIds = getEnqueuedTaskIds(response)
        if (seedTaskIds.length > 0) {
          // The seeding line rides on the save toast rather than being left to the task machinery:
          // this page has no ambient running indicator — `taskMessage` reaches only the blocked
          // modal — so registering the task otherwise locks the page's other actions with nothing
          // on screen to explain it. Same shape as the sync panels, which pair `setTask` with a
          // toast of their own. Found by review on #1939.
          const seedingMessage = response?.data?.message ?? 'Survey questions are being prepared in the background.'
          notification('Section created successfully', { variant: 'success', subTitle: seedingMessage })
          setTask(seedTaskIds, seedingMessage, 'Survey questions are ready.')
        } else {
          notification('Section created successfully', { variant: 'success' })
        }
      }

      await dispatch(getSlotSectionApi({ urlId: id, params: getRefreshParams() }))
      handleCloseModal()
    } catch (error) {
      setErrors(showServerFieldErrors(error, bindSetError(methods.setError), methods.clearErrors))
    } finally {
      setLoading(false)
    }
  }
  return (
    <FormProvider {...methods}>
      <Box mb={6} mt={5}>
        {(isEditAction && isAllowedOnTermsPage) && (
          <Alert icon={<WarningAmberIcon sx={{ color: red[900], fontSize: '24px' }} />} variant='outlined' severity='warning'>
            <AlertTitle color={red[900]} sx={{ fontWeight: 600, fontSize: '14px' }}>
              Course section details are managed within your Terms. To update these details, click <Link href={`${ROUTES.TERMS_DETAILS}${slot?.term?.termId ? `/${slot.term.termId}` : ''}`} color='inherit' variant='inherit'>here</Link>.
            </AlertTitle>
          </Alert>
        )}
      </Box>
      <ValidationAlert errors={errors} />
      <TabContext value={tab}>
        {isEditAction && (
          <TabList onChange={(e, v: string) => { setTab(v) }}>
            <Tab label="Details" value="1" />
            <Tab label='Student Enrollments' value="2" />
          </TabList>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <TabPanel
            value="1"
            sx={{
              px: 0,
              pt: isEditAction ? 3 : 0,
              pb: 0
            }}
          >

            <Box display="flex" flexDirection="column" gap={isEditAction ? theme.spacing(6) : theme.spacing(3)}>

              {!bulkUpdate && (
              <Box>
                <ApiAutoComplete
                  apiEndpoint={`/api/sections/summary/?slot_id=${slot?.slotId}&is_present=false`}
                  label="Course Section(s)"
                  labelField="title"
                  placeholder='Select course sections'
                  name="sectionId"
                  required
                  readOnly={isEditAction}
                  selectedItem={linkedSection}
                  skip={readonly}
                  queryOptions={{ staleTime: 0, refetchOnMount: true }}
                  getOptionLabel={(option: any) => option?.title ?? ''}
                  getOptionSubLabel={(option: any) => option?.abbreviation ?? ''}
                />

                {!isEditAction && (
                  <Box display="flex" justifyContent="flex-end" mt={theme.spacing(1)}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => {
                        const values = getValues()
                        setBulkSectionDates({
                          startTimestamp: values.startTimestamp,
                          endTimestamp: values.endTimestamp,
                          earlyAccessReleaseTimestamp1: values.earlyAccessReleaseTimestamp1,
                          earlyAccessReleaseTimestamp2: values.earlyAccessReleaseTimestamp2
                        })
                        setBulkAddCourseSectionOpen(true)
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(0.5),
                        cursor: 'pointer',
                        textDecoration: 'none',
                        color: 'primary.main',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Bulk Select
                      <ArrowForwardIcon sx={{ fontSize: '16px' }} />
                    </Link>
                  </Box>
                )}
              </Box>
              )}

              {isEditAction && (
                <>
                  <FormInput name="title" label="Section Title" control={control} required disabled={isEditAction} />
                  <MultiSelectApiAutoComplete
                    name='instructors'
                    apiEndpoint='/api/users/'
                    sortField='name'
                    getOptionLabel={(option: IUser) => option.name}
                    getOptionValue={(option: IUser) => option.userId}
                    getOptionSubLabel={(option: IUser) => option.email}
                    apiExtraParams={{ isFaculty: true }}
                    label='Instructor(s)'
                    disabled
                    skip={readonly}
                    getOptionDisabled={() => true}
                  />
                  {selectedTeachingAssistants.length > 0 && (
                    <MultiSelectApiAutoComplete
                      name='teachingAssistants'
                      apiEndpoint='/api/users/'
                      sortField='name'
                      getOptionLabel={(option: IUser) => option.name}
                      getOptionValue={(option: IUser) => option.userId}
                      getOptionSubLabel={(option: IUser) => option.email}
                      apiExtraParams={{ isFaculty: true }}
                      label='Teaching Assistant(s)'
                      disabled
                      skip={readonly}
                      getOptionDisabled={() => true}
                    />
                  )}
                  <FormInput name="studentCount" label="Enrollment" control={control} required disabled={isEditAction} />
                  <AttributesMultiSelect name="attributes" defaultAttributes={attributes} disabled skip={readonly} />
                </>
              )}
              <GenericDateTimePicker
                name="startTimestamp"
                label="Start Date"
                required={!bulkUpdate}
                clearable={bulkUpdate}
                disabled={!canEditStartDate}
                minDate={startMin}
                placeholder="Enter Start Date"
                onChange={() => {
                  lastChangedField.current = 'start'
                  void trigger(['endTimestamp'])
                }}
              />
              <GenericDateTimePicker
                name="endTimestamp"
                label="End Date"
                required={!bulkUpdate}
                clearable={bulkUpdate}
                disabled={!canEditEndDate}
                minDate={watch('startTimestamp') || new Date()}
                maxDate={new Date(slot.slotEndTimestamp)}
                placeholder="Enter End Date"
                onChange={() => {
                  lastChangedField.current = 'end'
                  void trigger(['startTimestamp', 'earlyAccessReleaseTimestamp1', 'earlyAccessReleaseTimestamp2'])
                }}
              />
              {slot.releaseTimestamp !== ReleaseMode.Rolling &&
                <>
                  <GenericDateTimePicker
                    name="earlyAccessReleaseTimestamp2"
                    label="Admin Release Date"
                    clearable={bulkUpdate}
                    disabled={!canEditReleaseDates}
                    minDate={watch('endTimestamp') || new Date()}
                    maxDate={!isRollingRelease && slot.releaseTimestamp ? new Date(slot.releaseTimestamp as string) : undefined}
                    placeholder="Enter Admin Release Date"
                    onChange={() => {
                      lastChangedField.current = 'earlyAccess2'
                      // endTimestamp has before-earlyAccess tests; earlyAccess1/2 have no cross-dependency on each other
                      void trigger(['endTimestamp'])
                    }}
                  />
                  <GenericDateTimePicker
                    name="earlyAccessReleaseTimestamp1"
                    label="Instructor Release Date"
                    disabled={!canEditReleaseDates}
                    clearable={bulkUpdate || isGradePostingReleaseEnabled}
                    minDate={watch('endTimestamp') || new Date()}
                    maxDate={!isRollingRelease && slot.releaseTimestamp ? new Date(slot.releaseTimestamp as string) : undefined}
                    placeholder="Enter Instructor Release Date"
                    onChange={() => {
                      lastChangedField.current = 'earlyAccess1'
                      // endTimestamp has before-earlyAccess tests; earlyAccess1/2 have no cross-dependency on each other
                      void trigger(['endTimestamp'])
                    }}
                  />
                  {!bulkUpdate && isGradePostingReleaseEnabled && (
                    <Typography variant="body2" color="text.secondary">
                      Optional. Leave unset to block instructor results until grades are posted and you set a release date manually.
                    </Typography>
                  )}
                </>}

              {bulkUpdate && (
                <Typography variant="body2" color="text.secondary">
                  Leave blank to keep each section&apos;s current value.
                </Typography>
              )}

              {bulkUpdate && isOptOutEnabled && (
                <CustomRadioButtonGroup
                  name="optOutState"
                  label="Opt-out"
                  control={control}
                  options={OPT_OUT_OPTIONS}
                />
              )}

              {bulkUpdate && (
                <Box sx={BulkSelectButtonStyle}>
                  <GenericButton
                    label="Bulk Select"
                    buttonType={ButtonType.Secondary}
                    fontWeight="600"
                    fullWidth
                    endIcon={<ArrowForwardIcon sx={{ fontSize: '16px' }} />}
                    onClick={() => {
                      const values = getValues()
                      setBulkSectionDates({
                        startTimestamp: values.startTimestamp,
                        endTimestamp: values.endTimestamp,
                        earlyAccessReleaseTimestamp1: values.earlyAccessReleaseTimestamp1,
                        earlyAccessReleaseTimestamp2: values.earlyAccessReleaseTimestamp2
                      })
                      setBulkUpdateOptedOut(resolveIsOptedOut(values.optOutState as OptOutState))
                      setBulkAddCourseSectionOpen(true)
                    }}
                  />
                </Box>
              )}
            </Box>
          </TabPanel>
          {tab === '1' && (
            <DefaultModalActions
              confirmButtonProps={{
                text: isEditAction ? 'Save' : 'Create',
                loading,
                type: 'submit',
                disabled: readonly || isSubmitting || !isDirty,
                tooltip: readonly ? READONLY_TOOLTIP : undefined,
                // Bulk Update has no single-section submit; the Bulk Select button
                // opens the selection modal which performs the save.
                hideButton: bulkUpdate
              }}
              destructiveButtonProps={{
                onClick: handleCloseModal
              }}
              lastUpdateMeta={isEditAction && (
                <LastUpdateMeta lastEditedBy={selectedRow?.linkedSection?.modifiedByUsername} lastEditedAt={selectedRow?.linkedSection?.modifiedOn} />
              )}

            />
          )}
        </form>
        {isEditAction &&
          <TabPanel value="2">
            <StudentsAndEnrollment
              termId={slot?.term?.termId ?? ''}
              abbreviation={abbreviation ?? ''}
              sectionId={sectionId ?? ''}
              onAllClose={handleCloseModal}
              readOnly
              slotId={slot.slotId}
              sectionStatus={sectionStatus}
            />
          </TabPanel>
        }
      </TabContext>
      {isBulkAddCourseSectionModalOpen && (
        <BulkSelectCourseSectionModal
          isOpen={isBulkAddCourseSectionModalOpen}
          onClose={() => { setBulkAddCourseSectionOpen(false) }}
          onParentClose={handleCloseModal}
          slotId={slot?.slotId}
          termId={slot?.term?.termId}
          sectionDates={bulkSectionDates}
          isRollingRelease={isRollingRelease}
          isPresent={bulkUpdate}
          isOptedOut={bulkUpdateOptedOut}
        />
      )}
    </FormProvider>
  )
}
